import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class LurveCartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound !== 'true') {
      this.addEventListener('click', this.onClick);
      this.dataset.bound = 'true';
    }
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
  }

  onCartLinesUpdate = (event) => {
    this.scheduleRestore();
    event.promise?.then(() => this.scheduleRestore()).catch(() => {});
  };

  async load() {
    const productId = this.dataset.productId;
    if (!productId || this.dataset.loading === 'true') return;

    this.dataset.loading = 'true';

    try {
      let products = this.readCache(productId);
      if (products.length < 4) {
        const complementary = await this.fetchRecommendations(productId, 'complementary');
        const related = await this.fetchRecommendations(productId, 'related');
        products = this.uniqueProducts([...products, ...complementary, ...related]).slice(0, 8);
        this.writeCache(productId, products);
      }
      if (!products.length) products = this.readCache('last-successful');
      if (!this.isConnected || !products.length) return;

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = cartResponse.ok ? await cartResponse.json() : { items: [] };
      const cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));

      const cards = products
        .map((product) => this.createCard(product, cartVariantIds))
        .filter(Boolean)
        .join('');

      if (!cards) return;

      this.innerHTML = `
        <div class="lurve-cart-recommendations__header">
          <h3 class="lurve-cart-recommendations__title">You might also like:</h3>
          <div class="lurve-cart-recommendations__navigation" aria-label="Product recommendations navigation">
            <button type="button" data-recommendations-previous aria-label="Previous products">&#8592;</button>
            <button type="button" data-recommendations-next aria-label="Next products">&#8594;</button>
          </div>
        </div>
        <div class="lurve-cart-recommendations__track">${cards}</div>
        <div class="lurve-cart-recommendations__status" role="status" aria-live="polite"></div>
      `;
      this.dataset.loaded = 'true';
      this.writeCache('last-successful', products);
      const track = this.querySelector('.lurve-cart-recommendations__track');
      if (track instanceof HTMLElement) {
        const updateNavigation = () => this.updateNavigation(track);
        track.addEventListener('scroll', updateNavigation, { passive: true });
        requestAnimationFrame(updateNavigation);
      }
    } catch (error) {
      console.warn('[lurve-cart-recommendations] Recommendations unavailable', error);
    } finally {
      delete this.dataset.loading;
    }
  }

  async fetchRecommendations(productId, intent) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  readCache(productId) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(`lurve-cart-recommendations:${productId}`) || '[]');
      return Array.isArray(cached) ? cached : [];
    } catch {
      return [];
    }
  }

  writeCache(productId, products) {
    if (!products.length) return;
    try {
      sessionStorage.setItem(`lurve-cart-recommendations:${productId}`, JSON.stringify(products));
    } catch {
      // Recommendations still work when session storage is unavailable.
    }
  }

  uniqueProducts(products) {
    const seen = new Set();
    return products.filter((product) => {
      const key = String(product?.id || product?.handle || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  createCard(product, cartVariantIds) {
    const variant = product.variants?.find((item) => item.available);
    if (!variant) return '';

    const optionNames = (product.options || []).map((option) =>
      typeof option === 'string' ? option : option?.name || ''
    );
    const sizeIndex = optionNames.findIndex((name) => /^(size|tamanho)$/i.test(name));
    const baseOptions = this.variantOptions(variant);
    const sizeVariants = sizeIndex >= 0
      ? (product.variants || []).filter((item) => {
          const options = this.variantOptions(item);
          return options.every((value, index) => index === sizeIndex || value === baseOptions[index]);
        })
      : [];
    const hasSizeChoices = sizeVariants.length > 1;
    const image = product.featured_image || product.images?.[0] || '';
    const imageUrl = typeof image === 'string' ? image : image?.src || '';
    const baseProductUrl = product.url || `/products/${product.handle}`;
    const productUrl = `${baseProductUrl}${baseProductUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
    const displayOptions = baseOptions.filter((value, index) => index !== sizeIndex && value);
    const variantTitle = displayOptions.join(' / ') || (variant.title && variant.title !== 'Default Title' ? variant.title : '');
    const isInCart = cartVariantIds.has(String(variant.id));
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price || 0);
    const currentPrice = Number(variant.price || 0);
    const priceMarkup = compareAtPrice > currentPrice
      ? `<span class="lurve-cart-recommendation__compare-price">${this.formatMoney(compareAtPrice)}</span><span class="lurve-cart-recommendation__sale-price">${this.formatMoney(currentPrice)}</span>`
      : `<span>${this.formatMoney(currentPrice)}</span>`;

    return `
      <article class="lurve-cart-recommendation">
        <a class="lurve-cart-recommendation__image-link" href="${this.escapeAttribute(productUrl)}">
          ${imageUrl ? `<img class="lurve-cart-recommendation__image" src="${this.escapeAttribute(this.resizeImage(imageUrl))}" alt="" loading="lazy" width="144" height="152">` : ''}
        </a>
        <div class="lurve-cart-recommendation__content">
          <a class="lurve-cart-recommendation__title" href="${this.escapeAttribute(productUrl)}">${this.escapeHtml(product.title)}</a>
          ${variantTitle ? `<span class="lurve-cart-recommendation__variant">${this.escapeHtml(variantTitle)}</span>` : ''}
          <div class="lurve-cart-recommendation__footer">
            <span class="lurve-cart-recommendation__price">${priceMarkup}</span>
            ${hasSizeChoices ? `<quick-add-component data-product-url="${this.escapeAttribute(productUrl)}">
              <button class="lurve-cart-recommendation__add" type="button" data-open-native-quick-buy aria-haspopup="dialog" aria-label="Choose options for ${this.escapeAttribute(product.title)}">Add to cart</button>
            </quick-add-component>` : `<button class="lurve-cart-recommendation__add" type="button" data-variant-id="${variant.id}" aria-label="${isInCart ? 'Already in cart' : `Add ${this.escapeAttribute(product.title)} to cart`}" ${isInCart ? 'disabled' : ''}>${isInCart ? 'Added' : 'Add to cart'}</button>`}
          </div>
        </div>
      </article>
    `;
  }

  onClick = (event) => {
    const navigationButton = event.target.closest('[data-recommendations-previous], [data-recommendations-next]');
    if (navigationButton instanceof HTMLButtonElement) {
      const track = this.querySelector('.lurve-cart-recommendations__track');
      if (track instanceof HTMLElement) {
        const direction = navigationButton.hasAttribute('data-recommendations-previous') ? -1 : 1;
        track.scrollBy({ left: direction * Math.max(track.clientWidth * 0.72, 150), behavior: 'smooth' });
      }
      return;
    }

    const nativeQuickBuyButton = event.target.closest('[data-open-native-quick-buy]');
    if (nativeQuickBuyButton instanceof HTMLButtonElement) {
      nativeQuickBuyButton.closest('quick-add-component')?.handleClick(event);
      return;
    }

    const button = event.target.closest('.lurve-cart-recommendation__add');
    if (!(button instanceof HTMLButtonElement)) return;
    this.addVariant(button);
  };

  updateNavigation(track) {
    const previous = this.querySelector('[data-recommendations-previous]');
    const next = this.querySelector('[data-recommendations-next]');
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (previous instanceof HTMLButtonElement) previous.disabled = track.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= maxScroll - 2 || maxScroll <= 2;
  }

  async addVariant(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding…';

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
      .filter(Boolean);
    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('sections', [...new Set(sectionIds)].join(','));
    formData.set('sections_url', window.location.pathname);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart-recommendation',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferredEventPromise.promise,
    }));

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add item');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: result.sections,
          source: 'lurve-cart-recommendations',
          didError: false,
        },
      });
      button.textContent = 'Added';
      button.disabled = true;
      button.closest('dialog')?.close();
      this.restoreAfterCartUpdate();
    } catch (error) {
      deferredEventPromise.reject(error);
      this.querySelector('.lurve-cart-recommendations__status').textContent = error.message || 'Unable to add item.';
      this.dispatchEvent(new CartErrorEvent({ error: error.message || 'Unable to add item', code: 'INVALID' }));
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  restoreAfterCartUpdate() {
    this.scheduleRestore();
  }

  scheduleRestore() {
    const restore = () => {
      document.querySelectorAll('lurve-cart-recommendations').forEach((component) => {
        if (component.querySelector('.lurve-cart-recommendations__track')) {
          component.dataset.loaded = 'true';
          return;
        }
        delete component.dataset.loading;
        component.load?.();
      });
    };

    [0, 80, 350, 900, 1800].forEach((delay) => window.setTimeout(restore, delay));
  }

  variantOptions(variant) {
    if (Array.isArray(variant?.options)) return variant.options.map(String);
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null).map(String);
  }

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  resizeImage(url) {
    return url.replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, '_360x$1$2');
  }

  escapeHtml(value = '') {
    const element = document.createElement('div');
    element.textContent = String(value);
    return element.innerHTML;
  }

  escapeAttribute(value = '') {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }
}

if (!customElements.get('lurve-cart-recommendations')) {
  customElements.define('lurve-cart-recommendations', LurveCartRecommendations);
}
